import { useRootStore } from "@/state/RootStore";
import { usePostHog } from "posthog-js/react";
import { type ReactNode, useCallback, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { APP_TO_BUILD } from "../../../appsConfig";
import {
  isReferralTrialActive,
  PLUMBING_DEFAULT_LEVEL_CODE,
} from "@petergok/blue-collar-shared";
import { showToastFromKey } from "@/lib/toast";
import * as Sentry from "@sentry/react";
import { Button } from "@/components/ui/button";
import {
  getPracticeTestTypesElectricalCA,
  getPracticeTestTypesElectricalUS,
  getPracticeTestTypesPlumbing,
  getPracticeTestTypesIbewAptitude,
  getCodebookHomeCard,
} from "@/utils/test-groups";
import PracticeItem from "./PracticeItem";
import { observer } from "mobx-react";
import CoachmarksModal from "./Coachmarks";
import TrialOfferModal from "@/components/referral/TrialOfferModal";
import TrialExpiredModal from "@/components/referral/TrialExpiredModal";
import { useToggle } from "react-use";
import UpgradeBanner from "@/components/UpgradeBanner";
import { useTranslation } from "react-i18next";
import { shouldShowPlumbingRedSealReferralTrialNotice } from "@/utils/appEntitlements";
import { useRedemptionPresentationBlocker } from "@/components/redemption/RedemptionPresentationContext";
import { QuestionGuidesEntryCard } from "@/components/question-guides/QuestionGuidesEntryCard";

// Define a type for the testTypesObj return values
type TestTypesResult = {
  quickPracticeTypes: any[];
  generalTestTypes: any[];
  categoryTestTypes?: any[];
  specialTestTypes?: any[];
};

interface MainStudyProps {
  promotionalBanner?: ReactNode;
}

export const MainStudy = observer(({ promotionalBanner }: MainStudyProps) => {
  const posthog = usePostHog();
  const navigate = useNavigate();
  const store = useRootStore();
  const { t } = useTranslation();

  const [showCoachmark, toggleShowCoachmark] = useToggle(
    store.mainAppStore.showCoachmark,
  );
  useRedemptionPresentationBlocker("coachmarks", showCoachmark);

  const stateSelected = store.mainAppStore.selectedStateCode || "";
  const referralTrial = store.mainAppStore.referralTrial();
  // Raw stored access, NOT hasStudyAccess()/effectiveAccess() — those merge
  // the active referral trial in, which would hide the paid upgrade.
  const storedPurchaseAccess =
    store.mainAppStore.newPurchaseState?.purchaseAccess;
  const showRedSealReferralTrialNotice =
    shouldShowPlumbingRedSealReferralTrialNotice({
      appToBuild: APP_TO_BUILD,
      referralTrialActive: isReferralTrialActive(referralTrial, Date.now()),
      selectedStateCode: store.mainAppStore.selectedStateCode,
      redSealStateCode: PLUMBING_DEFAULT_LEVEL_CODE,
      hasPaidStudyAccess: Boolean(
        storedPurchaseAccess?.studyAccess?.active ||
        storedPurchaseAccess?.allLevelStudyAccess?.active,
      ),
    });

  const inTrial = useMemo(() => {
    return !store.mainAppStore.hasStudyAccess(true);
  }, [
    store.mainAppStore.newPurchaseState,
    store.mainAppStore.selectedStateCode,
  ]);

  // Tell the student once if a stored attempt was too corrupt to reopen, rather than
  // letting it disappear with only a console.error.
  useEffect(() => {
    if (!store.testStore.activeTestRestoreFailed) return;
    // Reported here rather than at the failure site: that runs during RootStore
    // construction, before Sentry.init in main.tsx, so the capture would be dropped.
    if (store.testStore.activeTestRestoreError) {
      Sentry.captureException(store.testStore.activeTestRestoreError);
    }
    showToastFromKey({
      titleKey: "errors.generic_title",
      descriptionKey: "test.resume_failed_body",
      variant: "destructive",
    });
    store.testStore.clearActiveTestRestoreFailure();
  }, [store.testStore.activeTestRestoreFailed]);

  const codebookHomeCard = getCodebookHomeCard();

  // Restoration now happens in the TestsStore constructor, so /test is directly
  // refreshable. What used to be here ALSO force-navigated to /test after 1s, which made
  // the browser Back button unusable during an exam: Back landed on /study and the app
  // immediately pushed the student back into the test. The only escape was the Exit
  // button, which DISCARDS the attempt. Resuming is now the student's choice.
  const resumeActiveTest = useCallback(() => {
    // Re-point `activeTest` first: review mode shares that field, so if the student
    // opened a past result since starting, /test would render the review instead.
    if (store.testStore.resumeInProgressTest()) navigate("/test");
  }, [store, navigate]);

  const switchToRedSeal = async () => {
    await store.mainAppStore.setSelectedStateCode(PLUMBING_DEFAULT_LEVEL_CODE);
  };

  const testTypesObj = useMemo<TestTypesResult>(() => {
    if (APP_TO_BUILD === "electrical_us") {
      return getPracticeTestTypesElectricalUS();
    } else if (APP_TO_BUILD === "electrical") {
      return getPracticeTestTypesElectricalCA();
    } else if (APP_TO_BUILD === "ibew_aptitude") {
      return getPracticeTestTypesIbewAptitude();
    } else {
      return getPracticeTestTypesPlumbing();
    }
  }, []);

  const renderSectionHeader = (title: string) => {
    return (
      <h2 className="font-inter text-sm font-semibold leading-[21px] tracking-[0.07px] text-[#0a0c11]">
        {title}
      </h2>
    );
  };

  return (
    <>
      <div className="pb-10">
        <div className="flex flex-col gap-6">
          {/* Reads `inProgressTest`, NOT `activeTest`: `reviewTest()` reuses `activeTest`
              for the /test route, so opening a past result mid-exam would otherwise make
              this banner — the only way back into the attempt — silently disappear. */}
          {store.testStore.inProgressTest && (
            <div className="flex flex-col gap-3 rounded-xl border border-[var(--dp-brand-button)] bg-[var(--dp-brand-soft)] p-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-col gap-1">
                <h3 className="text-base font-semibold text-[#232323]">
                  {t("test.resume_title")}
                </h3>
                <p className="text-sm leading-5 text-[#666666]">
                  {t("test.resume_body")}
                </p>
              </div>
              <Button className="shrink-0" onClick={resumeActiveTest}>
                {t("test.resume_button")}
              </Button>
            </div>
          )}
          {!store.mainAppStore.hasStudyAccess(false) &&
            store.mainAppStore.isStudyTrialExpired() && (
            <UpgradeBanner
              header={t("study.trial_ended_header")}
              description={t("study.trial_ended_description")}
            />
            )}
          {showRedSealReferralTrialNotice && (
            <div className="flex flex-col gap-3 rounded-xl border border-[#F6D900] bg-[#FFFDEE] p-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-col gap-1">
                <h3 className="text-base font-semibold text-[#232323]">
                  {t("study.red_seal_referral_trial_title")}
                </h3>
                <p className="text-sm leading-5 text-[#666666]">
                  {t("study.red_seal_referral_trial_body")}
                </p>
              </div>
              <button
                type="button"
                onClick={switchToRedSeal}
                className="h-10 shrink-0 rounded-full bg-[var(--dp-brand-button)] px-5 text-sm font-semibold text-white hover:bg-[var(--dp-brand-hover)]"
              >
                {t("study.red_seal_referral_trial_cta")}
              </button>
            </div>
          )}
          {/* Practice Cards */}
          {/* Quick Practice Section */}
          {(testTypesObj.quickPracticeTypes?.length > 0 || codebookHomeCard !== null) && (
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
                  {testTypesObj.quickPracticeTypes?.map((i) => (
                    <PracticeItem
                      coachmarkTarget
                      key={i}
                      testType={i}
                      stateSelected={stateSelected}
                      inTrial={inTrial}
                      inRow
                    />
                  ))}
                  {codebookHomeCard !== null && (
                    <div>
                      <PracticeItem
                        coachmarkTarget
                        testType={codebookHomeCard}
                        stateSelected={stateSelected}
                        inTrial={inTrial}
                        inRow
                      />
                    </div>
                  )}
                  <QuestionGuidesEntryCard />
                </div>
            )}
          {promotionalBanner}
          {/* General Section - No Header */}
          <div className="flex flex-col gap-2">
            {(APP_TO_BUILD === "electrical_us" ||
              APP_TO_BUILD === "plumbing" ||
              APP_TO_BUILD === "electrical") &&
              renderSectionHeader(t("study.prep_mode_section"))}
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
              {testTypesObj.generalTestTypes?.map((i) => (
                <PracticeItem
                  key={i}
                  testType={i}
                  stateSelected={stateSelected}
                  inTrial={inTrial}
                />
              ))}
            </div>
          </div>
          {/* Category Modes Section */}
          {testTypesObj.categoryTestTypes &&
            testTypesObj.categoryTestTypes.length > 0 && (
              <div className="flex flex-col gap-2">
                {renderSectionHeader(t("study.category_modes_section"))}
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
                  {testTypesObj.categoryTestTypes?.map((i) => (
                    <PracticeItem
                      key={i}
                      testType={i}
                      stateSelected={stateSelected}
                      inTrial={inTrial}
                    />
                  ))}
                </div>
              </div>
            )}

          {/* Special Modes Section */}
          {testTypesObj.specialTestTypes &&
            testTypesObj.specialTestTypes.length > 0 && (
              <div className="flex flex-col gap-2">
                {renderSectionHeader(t("study.special_modes_section"))}
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
                  {testTypesObj.specialTestTypes?.map((i) => (
                    <PracticeItem
                      key={i}
                      testType={i}
                      stateSelected={stateSelected}
                      inTrial={inTrial}
                    />
                  ))}
                </div>
              </div>
            )}
        </div>
      </div>
      <CoachmarksModal
        open={showCoachmark}
        onOpenChange={toggleShowCoachmark}
      />
      <TrialOfferModal coachmarkActive={showCoachmark} />
      <TrialExpiredModal />
    </>
  );
});
